// pms-adapters / supabase / stayFeed — the Vega implementation of `StayFeed`.
//
// `docs/METRICS-FEED-BRIEF.md` §2 + §7 M2. This is the DISPOSABLE half of the
// design: it knows the incumbent's table names and nothing else does. When Vega
// is retired at end of March 2027 (O9) this file is deleted and a
// `PmsStayFeed` over `pms_room_stay` takes its place behind the same interface;
// no caller changes.
//
// ── Why the aggregation is in SQL and not here ─────────────────────────────
//
// `vega_stay_rollup` (migration 0125) does the per-day work. Pulling ~6000
// stays into a browser to group them by night would ship the seed over the
// wire to answer a question about one fortnight, and would put a second
// implementation of "which nights does this stay hold" in TypeScript — where
// it could disagree with the one the import report already uses. The database
// is the aggregate boundary (ARCHITECTURE.md §1); that applies to reads.
//
// What is left here is exactly what an adapter is for: naming the incumbent's
// objects, and translating snake_case rows into the port's domain types.

import {
  err,
  ok,
  type BoardDayFigures,
  type BoardFigureSource,
  type IsoDate,
  type PropertyCode,
  type Result,
  type StayDayBase,
  type StayFeed,
  type StayFeedProvenance,
} from '@lemontaigu/pms-core';
import { fromList, toPmsError, type SupabaseClient } from './support.js';

/** One row of `vega_stay_rollup`, verbatim. */
interface RollupRow {
  date: string;
  rooms_occupied: number;
  rooms_arrivals: number;
  rooms_departures: number;
  rooms_unallocated: number;
  guests_adults: number;
  guests_children: number;
  guests_arrivals: number;
  guests_departures: number;
  breakfasts: number;
  half_boards: number;
  parties_occupied: number;
  parties_arrivals: number;
  parties_departures: number;
  parties_breakfast: number;
  parties_half_board: number;
  parties_no_breakfast: number;
  guests_last_night: number;
  dinner_from_plan_parties: number;
  breakfast_from_plan_parties: number;
  guests_lodging_effectif: number;
  rooms_no_lodging: number;
}

/** The slice of `vega_import` provenance needs. */
interface ImportRow {
  created_at: string;
  source_mtime: string | null;
  window_start: string;
  window_end: string;
  status: string;
  refusal_reason: string | null;
}

const toBase = (r: RollupRow): StayDayBase => ({
  date: r.date,
  roomsOccupied: r.rooms_occupied,
  roomsArrivals: r.rooms_arrivals,
  roomsDepartures: r.rooms_departures,
  roomsUnallocated: r.rooms_unallocated,
  guestsAdults: r.guests_adults,
  guestsChildren: r.guests_children,
  guestsArrivals: r.guests_arrivals,
  guestsDepartures: r.guests_departures,
  breakfasts: r.breakfasts,
  halfBoards: r.half_boards,
  partiesOccupied: r.parties_occupied,
  partiesArrivals: r.parties_arrivals,
  partiesDepartures: r.parties_departures,
  partiesBreakfast: r.parties_breakfast,
  partiesHalfBoard: r.parties_half_board,
  partiesNoBreakfast: r.parties_no_breakfast,
  guestsLastNight: r.guests_last_night,
  dinnerFromPlanParties: r.dinner_from_plan_parties,
  breakfastFromPlanParties: r.breakfast_from_plan_parties,
  guestsLodgingEffectif: r.guests_lodging_effectif,
  roomsNoLodging: r.rooms_no_lodging,
});

export class SupabaseVegaStayFeed implements StayFeed {
  constructor(private readonly db: SupabaseClient) {}

  async dailyRollup(
    propertyCode: PropertyCode,
    from: IsoDate,
    to: IsoDate,
  ): Promise<Result<StayDayBase[]>> {
    // Reversed bounds are refused rather than silently returning nothing: an
    // empty rollup is indistinguishable from "the hotel was empty", and a
    // caller that swapped its arguments deserves to be told so.
    if (to < from) {
      return err('validation', `Plage de dates inversée (${from} → ${to}).`);
    }

    const r = await this.db.rpc('vega_stay_rollup', {
      p_property_code: propertyCode,
      p_from: from,
      p_to: to,
    });
    return fromList<RollupRow, StayDayBase>(
      r as { data: RollupRow[] | null; error: typeof r.error },
      toBase,
    );
  }

  async provenance(propertyCode: PropertyCode): Promise<Result<StayFeedProvenance>> {
    // The LAST attempt and the last APPLIED one are different rows whenever an
    // import was refused, and §6.2 needs both: the figures on screen come from
    // the applied one, while the refusal is what the operator has to act on.
    // Two ordered reads rather than one — a single "latest row" would show a
    // refusal's null window as though the feed had never run.
    const latest = await this.db
      .from('vega_import')
      .select('created_at, source_mtime, window_start, window_end, status, refusal_reason')
      .eq('property_code', propertyCode)
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (latest.error) return { ok: false, error: toPmsError(latest.error) };

    const last = latest.data as ImportRow | null;
    if (!last) {
      return ok({
        importedAt: null,
        sourceChangedAt: null,
        windowStart: null,
        windowEnd: null,
        lastAttemptApplied: true, // nothing has failed; nothing has run
        refusalReason: null,
      });
    }

    if (last.status === 'applied') {
      return ok({
        importedAt: last.created_at,
        sourceChangedAt: last.source_mtime,
        windowStart: last.window_start,
        windowEnd: last.window_end,
        lastAttemptApplied: true,
        refusalReason: null,
      });
    }

    const applied = await this.db
      .from('vega_import')
      .select('created_at, source_mtime, window_start, window_end, status, refusal_reason')
      .eq('property_code', propertyCode)
      .eq('status', 'applied')
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (applied.error) return { ok: false, error: toPmsError(applied.error) };

    const good = applied.data as ImportRow | null;
    return ok({
      importedAt: good?.created_at ?? null,
      sourceChangedAt: good?.source_mtime ?? null,
      windowStart: good?.window_start ?? null,
      windowEnd: good?.window_end ?? null,
      lastAttemptApplied: false,
      refusalReason: last.refusal_reason,
    });
  }

  async requestRefresh(propertyCode: PropertyCode, reason?: string): Promise<Result<void>> {
    // Migration 0122. Nothing outside the hotel LAN can reach the importer, so
    // this records a request and the importer collects it on its next tick
    // (every minute). It returns « asked », never « done » — see the port.
    //
    // ⚠ A TABLE INSERT, not an RPC. This called `.rpc('vega_import_request')`
    // until 2026-09-01, which 404s — 0122 creates a TABLE of that name with an
    // insert policy, and no function. The unit test asserted the RPC call, so
    // it pinned the mistake rather than catching it; only probing the deployed
    // schema did (`PGRST202: no matches found in the schema cache`).
    //
    // `requested_by` is left to its `auth.uid()` default and `served_at` to the
    // importer — a client may insert, and may do nothing else: 0122 grants no
    // update or delete policy, because a request is a fact and only the
    // importer records that it was served.
    const r = await this.db
      .from('vega_import_request')
      .insert({ property_code: propertyCode, reason: reason ?? null });
    if (r.error) return { ok: false, error: toPmsError(r.error) };
    return ok(undefined);
  }
}

// ── The board, read for comparison (M2 only) ────────────────────────────────
//
// The ACL over C3's `board_days`. It is the only place in C4 that knows that
// table's name or its column spellings, and it is written to be DELETED at M4
// along with the port it implements.

/** `board_days` as PostgREST returns it. */
interface BoardRow {
  date: string;
  occupancy: number | null;
  arrivals: number | null;
  departures: number | null;
  breakfasts: number | null;
  half_boards: number | null;
}

export class SupabaseBoardFigures implements BoardFigureSource {
  constructor(private readonly db: SupabaseClient) {}

  async figures(from: IsoDate, to: IsoDate): Promise<Result<BoardDayFigures[]>> {
    if (to < from) {
      return err('validation', `Plage de dates inversée (${from} → ${to}).`);
    }

    // ⚠ NOT scoped by property, because `board_days` has no property column —
    // it is keyed on `date` alone and has been single-établissement since 0117.
    // Stated rather than silently omitted: a `.eq('property_code', …)` here
    // would fail, and a caller reading this adapter is entitled to know the
    // board does not yet have the dimension the feed does. When C3 gains one,
    // this signature gains a property code and the compiler finds every caller.
    const r = await this.db
      .from('board_days')
      .select('date, occupancy, arrivals, departures, breakfasts, half_boards')
      .gte('date', from)
      .lte('date', to)
      .order('date');

    return fromList<BoardRow, BoardDayFigures>(
      r as { data: BoardRow[] | null; error: typeof r.error },
      (row) => ({
        date: row.date,
        occupancy: row.occupancy,
        arrivals: row.arrivals,
        departures: row.departures,
        breakfasts: row.breakfasts,
        halfBoards: row.half_boards,
      }),
    );
  }
}
